import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { get } from 'ravenjs/utils/lodash';

import NavItem from 'components/NavItem';
import withPublicContent from 'components/PublicContent/PublicContentHOC';
import { SHOW_NEW_SAFETY_LANDING_FLAG } from 'constants/content';
import { EVENTS } from 'constants/events';
import {
    DESKTOP_BREAKPOINT,
    DROPDOWN_OPEN_EVENT,
    EXTERNAL_ROUTES,
    HOVER_INTENT_DELAY,
    KEYBOARD_KEYS,
    NAV_STATE_PARENT_ROUTE_MAP,
    SUBMENU_CLOSE_DELAY,
} from 'constants/navigation';
import { DEFAULT_TITLE } from 'constants/safety';
import Acl from 'modules/acl';
import { actions as pendoActions } from 'modules/pendo';
import { actions as userActions, selectors as userSelectors } from 'modules/user';
import { selectors as authSelectors } from 'modules/auth';
import { actions as communityActions } from 'modules/community';
import { actions as contentfulActions } from 'modules/contentful';

import { navItemsNotBeClickable } from 'utils/navigation';
import { redirectExternalRoutes } from 'utils/user';
import { hasFeatureFlag } from 'utils/common';

import { MNL_INTELLIGENCE } from 'constants/intelligence';
import { NavItemsStyled } from './NavItemsStyled';

class NavItems extends Component {
    static propTypes = {
        history: object,
        isImpersonatingUser: bool,
        items: arrayOf(
            shape({
                title: string.isRequired,
                to: string,
                state: string,
            })
        ),
        isSoftLogin: bool.isRequired,
        isVisibleOnDOM: bool,
        openLoginPrompt: func.isRequired,
        sendPendoEvent: func.isRequired,
        updateClickSafetyMenu: func.isRequired,
        currentUser: object.isRequired,
        userCompany: object.isRequired,
        accessToken: string.isRequired,
        fetchCommunityUrl: func.isRequired,
        addVisitItem: func.isRequired,
    };

    static defaultProps = {
        history: {},
        isImpersonatingUser: false,
        items: [],
        isVisibleOnDOM: true,
    };

    navContainerRef = createRef();

    // Array to store refs for each nav item for keyboard navigation
    navItemRefs = [];

    constructor(props) {
        super(props);

        const subNavVisible = {};
        props.items.forEach(item => {
            subNavVisible[item.title] = false;
        });

        this.state = {
            scrollTop: null,
            subNavVisible,
            isDesktop: this.checkIsDesktop(),
            // To Track focused submenu item index for keyboard navigation
            focusedSubmenuItemIndex: -1,
        };

        this.hoverIntentTimeout = null;
        this.closeDelayTimeout = null;
    }

    componentDidMount() {
        globalThis.addEventListener('scroll', this.handleScroll);
        globalThis.addEventListener('resize', this.handleResize);
        document.addEventListener('mousedown', this.handleClickOutside);
        globalThis.addEventListener(DROPDOWN_OPEN_EVENT, this.handleOtherDropdownOpen);
        // Added document-level keyboard listener for submenu navigation
        document.addEventListener('keydown', this.handleSubmenuKeyDown);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { isVisibleOnDOM } = this.props;
        const { subNavVisible } = this.state;

        if (!isVisibleOnDOM) {
            const activeSubNav = Object.keys(subNavVisible).filter(function(key) {
                return subNavVisible[key];
            });
            activeSubNav[0] && this.hide(activeSubNav[0])();
        }
    }

    componentWillUnmount() {
        globalThis.removeEventListener('scroll', this.handleScroll);
        globalThis.removeEventListener('resize', this.handleResize);
        document.removeEventListener('mousedown', this.handleClickOutside);
        globalThis.removeEventListener(DROPDOWN_OPEN_EVENT, this.handleOtherDropdownOpen);
        this.clearAllTimeouts();
        // Removed document-level keyboard listener
        document.removeEventListener('keydown', this.handleSubmenuKeyDown);
    }

    isNavigationV2Enabled = () => {
        const { history } = this.props;
        return history?.location ? hasFeatureFlag(history.location, 'showNavigationV2') : false;
    };

    clearAllTimeouts = () => {
        if (this.hoverIntentTimeout) {
            clearTimeout(this.hoverIntentTimeout);
            this.hoverIntentTimeout = null;
        }
        if (this.closeDelayTimeout) {
            clearTimeout(this.closeDelayTimeout);
            this.closeDelayTimeout = null;
        }
    };

    // Handle keyboard events when the focus is on submenu items
    handleSubmenuKeyDown = event => {
        if (!this.isNavigationV2Enabled() || !this.state.isDesktop) {
            return;
        }

        const { items } = this.props;
        const { subNavVisible } = this.state;

        // Find which submenu is currently open
        const openSubmenuTitle = Object.keys(subNavVisible).find(key => subNavVisible[key]);
        if (!openSubmenuTitle) {
            return; // No submenu open, let the default behavior happen
        }

        const openSubmenuIndex = items.findIndex(item => item.title === openSubmenuTitle);

        // Check if the focus is on a submenu item
        const submenuItems = this.getSubmenuItems();
        const focusedSubmenuIndex = submenuItems.indexOf(document.activeElement);

        if (focusedSubmenuIndex < 0) {
            return;
        }

        switch (event.key) {
            case KEYBOARD_KEYS.ESCAPE:
                event.preventDefault();
                event.stopPropagation();
                this.clearAllTimeouts();
                this.hideSubNav();
                this.setState({ focusedSubmenuItemIndex: -1 });
                // Return focus to the nav item
                if (this.navItemRefs[openSubmenuIndex]) {
                    this.navItemRefs[openSubmenuIndex].focus();
                }
                break;

            case KEYBOARD_KEYS.ARROW_DOWN: {
                event.preventDefault();
                event.stopPropagation();
                const nextIndex =
                    focusedSubmenuIndex < submenuItems.length - 1 ? focusedSubmenuIndex + 1 : 0;
                this.setState({ focusedSubmenuItemIndex: nextIndex });
                submenuItems[nextIndex]?.focus();
                break;
            }

            case KEYBOARD_KEYS.ARROW_UP: {
                event.preventDefault();
                event.stopPropagation();
                const prevIndex =
                    focusedSubmenuIndex > 0 ? focusedSubmenuIndex - 1 : submenuItems.length - 1;
                this.setState({ focusedSubmenuItemIndex: prevIndex });
                submenuItems[prevIndex]?.focus();
                break;
            }

            default:
                break;
        }
    };

    checkIsDesktop = () => {
        return typeof globalThis !== 'undefined' && globalThis.innerWidth >= DESKTOP_BREAKPOINT;
    };

    // Handle window resize for V2 responsive behavior
    handleResize = () => {
        const isDesktop = this.checkIsDesktop();
        if (isDesktop !== this.state.isDesktop) {
            this.setState({ isDesktop });
            // Close all submenus when switching between desktop and mobile
            if (!isDesktop && this.isNavigationV2Enabled()) {
                this.clearAllTimeouts();
                this.hideSubNav();
            }
        }
    };

    // Handle click outside to close all submenus immediately
    handleClickOutside = event => {
        if (!this.isNavigationV2Enabled()) {
            return;
        }
        if (this.navContainerRef.current && !this.navContainerRef.current.contains(event.target)) {
            this.clearAllTimeouts();
            this.hideSubNav();
        }
    };

    handleOtherDropdownOpen = event => {
        if (!this.isNavigationV2Enabled()) {
            return;
        }
        // If another dropdown opened (not navitems), close all submenus immediately
        if (event.detail && event.detail.source !== 'navitems') {
            this.clearAllTimeouts();
            this.hideSubNav();
        }
    };

    // Dispatch event to notify other dropdowns that a nav submenu is opening
    notifyDropdownOpen = () => {
        globalThis.dispatchEvent(
            new CustomEvent(DROPDOWN_OPEN_EVENT, {
                detail: { source: 'navitems' },
            })
        );
    };

    handleScroll = event => {
        const scrollTop = get(event, 'target.scrollingElement.scrollTop', 0);

        this.setState({
            scrollTop,
        });
    };

    show = title => () => {
        const isNavV2Enabled = this.isNavigationV2Enabled();
        const { isDesktop } = this.state;

        // V2 behavior with hover-intent
        if (isNavV2Enabled) {
            // Clear any pending close timeout when entering a new item
            if (this.closeDelayTimeout) {
                clearTimeout(this.closeDelayTimeout);
                this.closeDelayTimeout = null;
            }

            // For desktop V2, use hover-intent delay
            if (isDesktop) {
                // Clear any existing hover intent timeout
                if (this.hoverIntentTimeout) {
                    clearTimeout(this.hoverIntentTimeout);
                }

                this.hoverIntentTimeout = setTimeout(() => {
                    const { subNavVisible: newSubNavVisible } = this.state;
                    Object.keys(newSubNavVisible).forEach(sn => {
                        newSubNavVisible[sn] = false;
                    });
                    newSubNavVisible[title] = true;
                    // Notify other dropdowns to close when this one opens
                    this.notifyDropdownOpen();
                    this.setState({
                        subNavVisible: newSubNavVisible,
                    });
                }, HOVER_INTENT_DELAY);
            } else {
                // Mobile/tablet - immediate show
                const { subNavVisible: newSubNavVisible } = this.state;
                Object.keys(newSubNavVisible).forEach(sn => {
                    newSubNavVisible[sn] = false;
                });
                newSubNavVisible[title] = true;
                this.notifyDropdownOpen();
                this.setState({
                    subNavVisible: newSubNavVisible,
                });
            }
        } else {
            // ORIGINAL V1 behavior - unchanged
            const { subNavVisible: newSubNavVisible } = this.state;
            Object.keys(newSubNavVisible).forEach(sn => {
                newSubNavVisible[sn] = false;
            });
            newSubNavVisible[title] = true;
            this.setState({
                subNavVisible: newSubNavVisible,
            });
        }
    };

    hide = title => () => {
        const isNavV2Enabled = this.isNavigationV2Enabled();
        const { isDesktop } = this.state;

        // V2 behavior with delay tolerance
        if (isNavV2Enabled) {
            // Clear hover intent if the user moves away before intent is confirmed
            if (this.hoverIntentTimeout) {
                clearTimeout(this.hoverIntentTimeout);
                this.hoverIntentTimeout = null;
            }

            // For desktop V2, use delay tolerance before closing
            if (isDesktop) {
                // Clear any existing close timeout
                if (this.closeDelayTimeout) {
                    clearTimeout(this.closeDelayTimeout);
                }

                this.closeDelayTimeout = setTimeout(() => {
                    const { subNavVisible: newSubNavVisible } = this.state;
                    newSubNavVisible[title] = false;
                    this.setState({
                        subNavVisible: newSubNavVisible,
                    });
                }, SUBMENU_CLOSE_DELAY);
            } else {
                // Mobile/tablet - immediate hide
                const { subNavVisible: newSubNavVisible } = this.state;
                newSubNavVisible[title] = false;
                this.setState({
                    subNavVisible: newSubNavVisible,
                });
            }
        } else {
            // ORIGINAL V1 behavior - unchanged
            const { subNavVisible: newSubNavVisible } = this.state;
            newSubNavVisible[title] = false;
            this.setState({
                subNavVisible: newSubNavVisible,
            });
        }
    };

    // Cancel close when re-entering the submenu area (V2 only)
    cancelClose = () => {
        if (this.closeDelayTimeout) {
            clearTimeout(this.closeDelayTimeout);
            this.closeDelayTimeout = null;
        }
    };

    sendEvent = item => {
        const { sendPendoEvent, userCompany, currentUser } = this.props;
        const pendoEvent = EVENTS.NAVIGATION.SELECT;
        if (item.state === MNL_INTELLIGENCE.MINTEL_NAV_STATE) {
            const intelligencePendoEvent = EVENTS.INTELLIGENCE.REDIRECT_TO_DASHBOARD;
            intelligencePendoEvent.eventProperties.source = 'HR Compliance';
            intelligencePendoEvent.eventProperties.userName = currentUser && currentUser.userName;
            intelligencePendoEvent.eventProperties.companyId = userCompany.companyId;
            intelligencePendoEvent.eventProperties.companyName = userCompany.companyName;
            sendPendoEvent(intelligencePendoEvent);
        }
        pendoEvent.eventProperties.selection = item.state;
        sendPendoEvent(pendoEvent);
    };

    hideAndClick = item => id => {
        const {
            accessToken,
            fetchCommunityUrl,
            history,
            isSoftLogin,
            openLoginPrompt,
            updateClickSafetyMenu,
            addVisitItem,
        } = this.props;
        const itemsNotBeClickable = navItemsNotBeClickable(Acl);
        if (isSoftLogin) {
            openLoginPrompt({ redirectUrl: item.to });
        } else {
            this.clearAllTimeouts();

            const { subNavVisible: newSubNavVisible } = this.state;
            newSubNavVisible[item.title] = false;
            this.setState({
                subNavVisible: newSubNavVisible,
            });

            if (EXTERNAL_ROUTES.indexOf(item.to) !== -1) {
                const params = {
                    accessToken,
                    apiToCall: fetchCommunityUrl,
                    addVisitItem,
                };
                redirectExternalRoutes(item.to, params);
            } else if (id === item.title && itemsNotBeClickable.indexOf(item.state) === -1) {
                history.push(item.to);
                this.sendEvent(item);

                if (item.state === DEFAULT_TITLE.toLowerCase()) {
                    updateClickSafetyMenu(true);
                }
            }
        }
    };

    hideSubNav = () => {
        const { subNavVisible: newSubNavVisible } = this.state;
        Object.keys(newSubNavVisible).forEach(sn => {
            newSubNavVisible[sn] = false;
        });
        this.setState({
            subNavVisible: newSubNavVisible,
        });
    };

    // Handle mouse leave from the entire nav container (V2 only)
    handleNavContainerMouseLeave = () => {
        const isNavV2Enabled = this.isNavigationV2Enabled();
        const { isDesktop } = this.state;

        if (isNavV2Enabled) {
            // Clear hover intent if user moves away
            if (this.hoverIntentTimeout) {
                clearTimeout(this.hoverIntentTimeout);
                this.hoverIntentTimeout = null;
            }

            // For desktop V2, use delay tolerance before closing all
            if (isDesktop) {
                if (this.closeDelayTimeout) {
                    clearTimeout(this.closeDelayTimeout);
                }

                this.closeDelayTimeout = setTimeout(() => {
                    this.hideSubNav();
                }, SUBMENU_CLOSE_DELAY);
            } else {
                this.hideSubNav();
            }
        } else {
            this.hideSubNav();
        }
    };

    // Handle keyboard navigation for the navigation menu
    handleKeyDown = event => {
        if (!this.isNavigationV2Enabled() || !this.state.isDesktop) {
            return;
        }

        const { items } = this.props;
        const { subNavVisible } = this.state;

        // Find which submenu is currently open
        const openSubmenuTitle = Object.keys(subNavVisible).find(key => subNavVisible[key]);
        const openSubmenuIndex = openSubmenuTitle
            ? items.findIndex(item => item.title === openSubmenuTitle)
            : -1;

        switch (event.key) {
            case KEYBOARD_KEYS.ESCAPE:
                // Only handle Escape when a submenu is open
                if (openSubmenuIndex >= 0) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.handleEscapeKey(openSubmenuIndex);
                }
                break;

            case KEYBOARD_KEYS.ENTER:
            case KEYBOARD_KEYS.SPACE:
                event.preventDefault();
                event.stopPropagation();
                this.handleEnterOrSpaceKey();
                break;

            case KEYBOARD_KEYS.ARROW_DOWN:
                if (openSubmenuIndex >= 0) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.handleArrowDownKey(openSubmenuIndex);
                }
                break;

            case KEYBOARD_KEYS.ARROW_UP:
                if (openSubmenuIndex >= 0) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.handleArrowUpKey(openSubmenuIndex);
                }
                break;

            case KEYBOARD_KEYS.ARROW_LEFT:
                event.preventDefault();
                event.stopPropagation();
                this.handleArrowLeftKey();
                break;

            case KEYBOARD_KEYS.ARROW_RIGHT:
                event.preventDefault();
                event.stopPropagation();
                this.handleArrowRightKey();
                break;

            case KEYBOARD_KEYS.TAB:
                // WCAG: When Tab is pressed, close any open dropdown
                // This ensures Tab navigates between top-level items, not within dropdowns
                if (openSubmenuIndex >= 0) {
                    this.handleEscapeKey(openSubmenuIndex);
                }
                break;

            default:
                break;
        }
    };

    // Handle Escape key - close the submenu and return focus to parent
    handleEscapeKey = openSubmenuIndex => {
        this.clearAllTimeouts();
        this.hideSubNav();

        // Reset the focused submenu item
        this.setState({ focusedSubmenuItemIndex: -1 });

        // Return focus to the parent nav item if one was open
        if (openSubmenuIndex >= 0 && this.navItemRefs[openSubmenuIndex]) {
            this.navItemRefs[openSubmenuIndex].focus();
        }
    };

    // Handle focus on nav item - close other submenus immediately
    handleNavItemFocus = focusedTitle => {
        if (!this.isNavigationV2Enabled()) {
            return;
        }

        const { subNavVisible } = this.state;
        const openSubmenuTitle = Object.keys(subNavVisible).find(key => subNavVisible[key]);

        // If a different submenu is open, close it immediately
        if (openSubmenuTitle && openSubmenuTitle !== focusedTitle) {
            this.clearAllTimeouts();
            this.hideSubNav();
            this.setState({ focusedSubmenuItemIndex: -1 });
        }
    };

    handleEnterOrSpaceKey = () => {
        const { items } = this.props;
        const { focusedSubmenuItemIndex, subNavVisible } = this.state;

        // Find which submenu is currently open
        const openSubmenuTitle = Object.keys(subNavVisible).find(key => subNavVisible[key]);
        const openSubmenuIndex = openSubmenuTitle
            ? items.findIndex(item => item.title === openSubmenuTitle)
            : -1;

        // Find the currently focused nav item
        const navItemIndex = this.navItemRefs.indexOf(document.activeElement);

        // Check if the focus is on a submenu item
        const submenuItems = this.getSubmenuItems();
        const focusedSubmenuItemFromDOM = submenuItems.indexOf(document.activeElement);

        // If the focus is on a submenu item, activate it (click it)
        if (openSubmenuIndex >= 0 && focusedSubmenuItemFromDOM >= 0) {
            submenuItems[focusedSubmenuItemFromDOM].click();
            return;
        }

        // If the focus is on a DIFFERENT nav item than the open submenu,
        // close the old submenu and open the new one
        if (navItemIndex >= 0 && navItemIndex !== openSubmenuIndex) {
            // Close any open submenu first
            this.clearAllTimeouts();
            this.hideSubNav();

            // Reset the focused submenu item
            this.setState({ focusedSubmenuItemIndex: -1 });

            const item = items[navItemIndex];
            if (item && item.subnav && Object.keys(item.subnav).length > 0) {
                // Open the new submenu
                this.notifyDropdownOpen();
                const { subNavVisible: newSubNavVisible } = this.state;
                Object.keys(newSubNavVisible).forEach(sn => {
                    newSubNavVisible[sn] = false;
                });
                newSubNavVisible[item.title] = true;
                this.setState(
                    {
                        subNavVisible: newSubNavVisible,
                        focusedSubmenuItemIndex: 0,
                    },
                    () => {
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                this.focusSubmenuItem(0);
                            }, 100);
                        });
                    }
                );
            } else {
                // No submenu, navigate to the item's route
                this.hideAndClick(item)(item.title);
            }
            return;
        }

        // If a submenu is open AND the focus is on the SAME nav item AND a submenu item is focused, activate it
        if (
            openSubmenuIndex >= 0 &&
            navItemIndex === openSubmenuIndex &&
            focusedSubmenuItemIndex >= 0
        ) {
            const submenuItems = this.getSubmenuItems();
            if (submenuItems && submenuItems[focusedSubmenuItemIndex]) {
                submenuItems[focusedSubmenuItemIndex].click();
            }
            return;
        }

        // If the focus is on a nav item with no submenu open, open its submenu
        if (navItemIndex >= 0) {
            const item = items[navItemIndex];
            if (item && item.subnav && Object.keys(item.subnav).length > 0) {
                // Open the submenu
                this.clearAllTimeouts();
                this.notifyDropdownOpen();
                const { subNavVisible: newSubNavVisible } = this.state;
                Object.keys(newSubNavVisible).forEach(sn => {
                    newSubNavVisible[sn] = false;
                });
                newSubNavVisible[item.title] = true;
                this.setState(
                    {
                        subNavVisible: newSubNavVisible,
                        focusedSubmenuItemIndex: 0,
                    },
                    () => {
                        // Focus the first submenu item after opening
                        // Use requestAnimationFrame for better timing
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                this.focusSubmenuItem(0);
                            }, 100);
                        });
                    }
                );
            } else {
                // No submenu, navigate to the item's route
                this.hideAndClick(item)(item.title);
            }
        }
    };

    // Handle Arrow Down key - navigate down within the submenu
    handleArrowDownKey = openSubmenuIndex => {
        if (openSubmenuIndex < 0) {
            return;
        }

        const { focusedSubmenuItemIndex } = this.state;
        const submenuItems = this.getSubmenuItems();

        if (!submenuItems || submenuItems.length === 0) {
            return;
        }

        // Find the current focused index from DOM if the state is out of sync
        const currentFocusedIndex = submenuItems.indexOf(document.activeElement);
        const startIndex = currentFocusedIndex >= 0 ? currentFocusedIndex : focusedSubmenuItemIndex;

        const nextIndex = startIndex < submenuItems.length - 1 ? startIndex + 1 : 0; // Wrap to the first item

        this.setState({ focusedSubmenuItemIndex: nextIndex }, () => {
            this.focusSubmenuItem(nextIndex);
        });
    };

    // Handle Arrow Up key - navigate up within the submenu
    handleArrowUpKey = openSubmenuIndex => {
        if (openSubmenuIndex < 0) {
            return;
        }

        const { focusedSubmenuItemIndex } = this.state;
        const submenuItems = this.getSubmenuItems();

        if (!submenuItems || submenuItems.length === 0) {
            return;
        }

        // Find the current focused index from DOM if the state is out of sync
        const currentFocusedIndex = submenuItems.indexOf(document.activeElement);
        const startIndex = currentFocusedIndex >= 0 ? currentFocusedIndex : focusedSubmenuItemIndex;

        const prevIndex = startIndex > 0 ? startIndex - 1 : submenuItems.length - 1; // Wrap to last item

        this.setState({ focusedSubmenuItemIndex: prevIndex }, () => {
            this.focusSubmenuItem(prevIndex);
        });
    };

    handleArrowLeftKey = () => {
        const { items } = this.props;

        if (items.length === 0) {
            return;
        }

        // Find the currently focused nav item index
        const currentIndex = this.navItemRefs.indexOf(document.activeElement);

        // Close the current submenu if open
        this.clearAllTimeouts();
        this.hideSubNav();

        // Calculate previous index (wrap to last if at first)
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;

        // Focus the previous nav item
        if (this.navItemRefs[prevIndex]) {
            this.navItemRefs[prevIndex].focus();
        }

        this.setState({ focusedSubmenuItemIndex: -1 });
    };

    handleArrowRightKey = () => {
        const { items } = this.props;

        if (items.length === 0) {
            return;
        }

        // Find the currently focused nav item index
        const currentIndex = this.navItemRefs.indexOf(document.activeElement);

        // Close the current submenu if open
        this.clearAllTimeouts();
        this.hideSubNav();

        // Calculate the next index (wrap to first if at last)
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;

        // Focus the next nav item
        if (this.navItemRefs[nextIndex]) {
            this.navItemRefs[nextIndex].focus();
        }

        this.setState({ focusedSubmenuItemIndex: -1 });
    };

    // Get focusable submenu items within the nav container
    getSubmenuItems = () => {
        if (this.navContainerRef.current) {
            // Find all elements with the data-submenu-item attribute
            const submenuItems = this.navContainerRef.current.querySelectorAll(
                '[data-submenu-item="true"]'
            );
            return Array.from(submenuItems);
        }
        return [];
    };

    // Focus a specific submenu item by index
    focusSubmenuItem = index => {
        const submenuItems = this.getSubmenuItems();
        if (submenuItems && submenuItems[index]) {
            submenuItems[index].focus();
        }
    };

    // Store ref for nav item
    setNavItemRef = (index, ref) => {
        this.navItemRefs[index] = ref;
    };

    isNavItemSelected = ({ state }) => {
        const pathname = get(this.props, 'history.location.pathname', '');
        const search = get(this.props, 'history.location.search', '');
        const navItem =
            search && search.indexOf(SHOW_NEW_SAFETY_LANDING_FLAG) !== -1
                ? `/${pathname.split('/')[1]}${search}`
                : `/${pathname.split('/')[1]}`;
        const targetRoute = NAV_STATE_PARENT_ROUTE_MAP[state];
        return navItem.indexOf('?') !== -1
            ? navItem === targetRoute
            : navItem.indexOf(targetRoute) !== -1;
    };

    render() {
        const { items, isImpersonatingUser, isVisibleOnDOM, history } = this.props;
        const { scrollTop, subNavVisible, isDesktop } = this.state;

        const isNavigationV2Enabled = history?.location
            ? hasFeatureFlag(history.location, 'showNavigationV2')
            : false;

        return (
            <NavItemsStyled
                ref={this.navContainerRef}
                onMouseLeave={this.handleNavContainerMouseLeave}
                onKeyDown={this.isNavigationV2Enabled() ? this.handleKeyDown : undefined}
            >
                {items.map((item, index) => (
                    <NavItem
                        selected={this.isNavItemSelected(item)}
                        onMouseEnter={this.show(item.title)}
                        onMouseLeave={this.hide(item.title)}
                        onMouseOut={this.hide(item.title)}
                        onBlur={this.hide(item.title)}
                        onClick={this.hideAndClick(item)}
                        key={item.state}
                        isImpersonatingUser={isImpersonatingUser}
                        isSubNavVisibleOnDOM={isVisibleOnDOM}
                        showSubNav={subNavVisible[item.title]}
                        subNavScrollTop={scrollTop}
                        history={history}
                        onSubmenuMouseEnter={isNavigationV2Enabled ? this.cancelClose : undefined}
                        onSubmenuMouseLeave={
                            isNavigationV2Enabled ? this.handleNavContainerMouseLeave : undefined
                        }
                        isDesktop={isDesktop}
                        onFocus={() => this.handleNavItemFocus(item.title)}
                        navItemRef={ref => this.setNavItemRef(index, ref)}
                        tabIndex={isNavigationV2Enabled ? 0 : undefined}
                        {...item}
                    />
                ))}
            </NavItemsStyled>
        );
    }
}

const mapDispatchToProps = {
    sendPendoEvent: pendoActions.sendPendoEvent,
    updateClickSafetyMenu: userActions.updateClickSafetyMenu,
    fetchCommunityUrl: communityActions.fetchCommunityUrl,
    addVisitItem: contentfulActions.addVisitItem,
};

const mapStateToProps = createStructuredSelector({
    currentUser: userSelectors.getUserInfo,
    userCompany: userSelectors.getUserCompany,
    accessToken: authSelectors.getAccessToken,
});

export { NavItems as NavItemsUnwrapped };
export default withPublicContent(connect(mapStateToProps, mapDispatchToProps)(NavItems));
